/**
 * Mon planning — calendrier hebdomadaire des interventions du technicien.
 * Alimenté par ses rendez-vous confirmés (acceptés / réalisés) qui portent un
 * créneau. Le travail (accepter, clôturer, compte rendu) se fait dans « Mes
 * dossiers » ; ici c'est une vue de planification en lecture.
 */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import { listAssignedAppointments } from '@/features/appointments/appointments-api';

const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

/** Lundi 00:00 de la semaine contenant `d`. */
function startOfWeek(d: Date): Date {
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (date.getDay() + 6) % 7; // 0 = lundi
  date.setDate(date.getDate() - day);
  return date;
}

/** Clé locale YYYY-MM-DD (pas d'UTC, pour matcher l'affichage). */
function dayKey(d: Date): string {
  return d.toLocaleDateString('en-CA');
}

export function TechnicianPlanningPage() {
  const assigned = useQuery({
    queryKey: ['technician', 'assigned'],
    queryFn: listAssignedAppointments,
  });

  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(anchor);
    d.setDate(anchor.getDate() + i);
    return d;
  });

  const todayKey = dayKey(new Date());
  const events = (assigned.data ?? []).filter(
    (a) =>
      a.confirmedSlot && (a.status === 'accepte' || a.status === 'realise'),
  );
  const byDay = new Map<string, typeof events>();
  for (const e of events) {
    const key = dayKey(new Date(e.confirmedSlot!));
    const list = byDay.get(key) ?? [];
    list.push(e);
    byDay.set(key, list);
  }

  function shift(weeks: number) {
    const next = new Date(anchor);
    next.setDate(anchor.getDate() + weeks * 7);
    setAnchor(next);
  }

  return (
    <DashboardLayout variant="technicien" title="Mon planning">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Mon planning</CardTitle>
            <CardDescription>
              Semaine du {days[0]!.toLocaleDateString('fr-FR')} au{' '}
              {days[6]!.toLocaleDateString('fr-FR')}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => shift(-1)}>
              Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAnchor(startOfWeek(new Date()))}
            >
              Aujourd'hui
            </Button>
            <Button variant="outline" size="sm" onClick={() => shift(1)}>
              Suivant
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-7">
            {days.map((d, i) => {
              const key = dayKey(d);
              const isToday = key === todayKey;
              const items = byDay.get(key) ?? [];
              return (
                <div
                  key={key}
                  className={`min-h-32 rounded-md border p-2 ${
                    isToday ? 'border-bcl-blue bg-bcl-blue/5' : 'bg-white'
                  }`}
                >
                  <div className="mb-2 text-xs font-semibold text-gray-500">
                    {DAY_LABELS[i]} {d.getDate()}
                  </div>
                  <div className="space-y-1">
                    {items.map((e) => (
                      <Link
                        key={e.id}
                        to={`/technicien/projects`}
                        className="block rounded bg-emerald-100 px-2 py-1 text-[11px] text-emerald-900 hover:bg-emerald-200"
                      >
                        <div className="font-medium">
                          {new Date(e.confirmedSlot!).toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                        <div className="truncate">
                          {e.fiche
                            ? `${e.fiche.client.firstName} ${e.fiche.client.lastName}`
                            : (e.projectReference ?? 'Intervention')}
                        </div>
                      </Link>
                    ))}
                    {items.length === 0 && (
                      <p className="text-[11px] text-gray-300">—</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
